/**
 * Un numero che è una porta — gemello di quello del Cruscotto Commesse.
 *
 * La regola che tiene in piedi un cruscotto in questo progetto: **ogni numero
 * apre qualcosa, e dice dove porta**. Senza quella riga sotto il riquadro sembra
 * una statistica e nessuno ci clicca; e una pagina di sole statistiche qui è già
 * morta due volte (il Cruscotto del #9 e la dashboard logistica).
 */
import { ArrowRight } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import { num } from "@/pages/M2/format";
import { cn } from "@/lib/utils";

export type TonoPorta = "neutro" | "ok" | "attenzione" | "allarme";

const COLORE: Record<TonoPorta, string> = {
  neutro: "text-foreground",
  ok: "text-emerald-700 dark:text-emerald-400",
  attenzione: "text-amber-700 dark:text-amber-400",
  allarme: "text-red-700 dark:text-red-400",
};

export function Porta({
  label,
  valore,
  dove,
  sotto,
  tono = "neutro",
  icona: Icona,
  onClick,
  loading,
  inerte,
  titolo,
}: {
  label: string;
  valore: number | string | undefined;
  /** Dove porta il clic, in parole: «apre il piano». */
  dove: string;
  /** Riga di contesto sotto il numero, es. «~18 h di fermo macchina». */
  sotto?: string;
  tono?: TonoPorta;
  icona?: LucideIcon;
  onClick?: () => void;
  loading?: boolean;
  inerte?: boolean;
  /** Tooltip: qui ci va **come si calcola** il numero, così è controllabile. */
  titolo?: string;
}) {
  return (
    <button
      type="button"
      onClick={inerte ? undefined : onClick}
      disabled={inerte}
      title={titolo}
      className={cn(
        "group rounded-lg border bg-card p-3 text-left transition-colors",
        inerte
          ? "cursor-default opacity-70"
          : "hover:border-brand-600 hover:bg-brand-50/50 dark:hover:bg-brand-950/30"
      )}
    >
      <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        {Icona && <Icona className="size-3" aria-hidden="true" />}
        {label}
      </span>
      {loading ? (
        <Skeleton className="my-1 h-7 w-12" />
      ) : (
        <span
          className={cn(
            "block text-2xl font-semibold leading-tight tabular-nums",
            COLORE[tono]
          )}
        >
          {typeof valore === "number" ? num(valore) : (valore ?? "—")}
        </span>
      )}
      {sotto && <span className="mt-0.5 block text-[10.5px] text-muted-foreground">{sotto}</span>}
      <span className="mt-0.5 flex items-center gap-1 text-[10px] text-brand-700 dark:text-brand-400">
        {dove}
        {!inerte && (
          <ArrowRight className="size-2.5 opacity-0 transition-opacity group-hover:opacity-100" />
        )}
      </span>
    </button>
  );
}
